/**
 * Install state of the web app (PWA) and the browser's install prompt, when it has one.
 *
 * Two very different worlds hide behind one small API:
 *
 *   - Chromium browsers (Chrome / Edge / Samsung Internet on Android, Chrome on desktop)
 *     fire `beforeinstallprompt` once the site qualifies. We swallow the event and keep
 *     it, so the app can show its own nudge and call `prompt()` from a tap later. The
 *     event fires early - often before React has mounted - so `captureInstallEvents`
 *     has to run from the entry file, not from a component.
 *   - iOS has no programmatic install at all. The player adds the app from the share
 *     sheet; all we can do is tell them how. Inside Facebook / Instagram and other in-app
 *     browsers there is no share sheet either, so there we stay quiet.
 *
 * Whether the app is *already* installed is only knowable from inside the installed app
 * (`display-mode: standalone`). A Safari tab cannot tell, and the app deliberately keeps no
 * server-side record of it (one user is shared across the ETK platform), so the nudge
 * policy (installNudgePolicy.ts) works from local, best-effort state only.
 *
 * Safe to import from native code: every browser touch is guarded.
 */
import { detectBrowserPlatform, isInAppBrowser } from '../../utils/browserPlatform';

/** The way this browser can install the app right now. */
export type InstallMethod =
	/** Chromium: we hold a `beforeinstallprompt` event and can show the native dialog. */
	| 'native'
	/** iOS browser with a share sheet: show the "Add to Home Screen" instructions. */
	| 'manual-ios'
	/** Already installed, desktop, in-app browser, or Chromium has not offered one. */
	| 'none';

export type NativePromptOutcome = 'accepted' | 'dismissed' | 'unavailable';

type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const hasWindow = typeof window !== 'undefined';

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
let captured = false;
const listeners = new Set<() => void>();

const notify = () => {
	listeners.forEach(listener => {
		try {
			listener();
		} catch (error) {
			console.warn('[pwa] install listener failed:', error);
		}
	});
};

/**
 * Starts listening for the browser's install events. Call once, as early as possible
 * (src/entry/index.web.tsx); later calls are no-ops.
 */
export function captureInstallEvents(): void {
	if (!hasWindow || captured) return;
	captured = true;

	window.addEventListener('beforeinstallprompt', event => {
		// Without this Chrome shows its own mini-infobar at a time of its choosing.
		event.preventDefault();
		deferredPrompt = event as BeforeInstallPromptEvent;
		notify();
	});

	window.addEventListener('appinstalled', () => {
		deferredPrompt = null;
		installedThisSession = true;
		notify();
	});
}

/** Re-runs `listener` whenever the install method may have changed. Returns the unsubscribe. */
export function subscribeToInstallChanges(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** True when running as an installed app (home-screen launch), on any platform. */
export function isStandaloneDisplay(): boolean {
	if (!hasWindow) return false;
	try {
		if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
		if (typeof window.matchMedia !== 'function') return false;
		return (
			window.matchMedia('(display-mode: standalone)').matches ||
			window.matchMedia('(display-mode: fullscreen)').matches ||
			window.matchMedia('(display-mode: minimal-ui)').matches
		);
	} catch {
		return false;
	}
}

/** True once the browser reported the app installed during this page's lifetime. */
export const wasInstalledThisSession = () => installedThisSession;

/**
 * How the app can be installed from this browser right now. Phones only: desktop Chrome
 * can install too, but the nudge is aimed at phones, where the browser chrome costs the
 * game its full screen and its audio behaves worse.
 */
export function getInstallMethod(): InstallMethod {
	if (!hasWindow) return 'none';
	if (isStandaloneDisplay() || installedThisSession) return 'none';

	switch (detectBrowserPlatform()) {
		case 'android':
			return deferredPrompt ? 'native' : 'none';
		case 'ios':
			return isInAppBrowser() ? 'none' : 'manual-ios';
		default:
			return 'none';
	}
}

/**
 * Shows the browser's install dialog. Must be called from a user gesture (a tap handler);
 * Chrome refuses otherwise. The stored event is single-use, so after either answer the
 * method drops back to `none` until the browser offers a new one.
 */
export async function promptNativeInstall(): Promise<NativePromptOutcome> {
	const event = deferredPrompt;
	if (!event) return 'unavailable';
	deferredPrompt = null;

	try {
		await event.prompt();
		const choice = await event.userChoice;
		return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
	} catch (error) {
		console.warn('[pwa] native install prompt failed:', error);
		return 'unavailable';
	} finally {
		notify();
	}
}
